import { debugLog, errorLog } from '../core/utils/logger';
import { SigningAdapter, type SignL1ActionParams } from './base-adapter';
import { EIP712_DOMAINS, EIP712_TYPES } from '../core/constants';

export interface PrivyServerAdapterConfig {
  /** Privy user ID (DID) */
  userId: string;
  /** Delegated wallet address */
  walletAddress: string;
  /** Privy app ID */
  appId: string;
  /** Privy app secret */
  appSecret: string;
  /** Optional: Privy API base URL (defaults to production) */
  apiUrl?: string;
}

interface SignTypedDataRequest {
  chain_type: 'ethereum' | 'solana';
  typed_data: Record<string, unknown>;
  address: string;
}

interface SignTypedDataResponse {
  signature: string;
}

/**
 * Signing adapter for backend using Privy's delegated wallet API
 * Uses server-side API to sign on behalf of users with delegated wallets
 */
export class PrivyServerAdapter extends SigningAdapter {
  private config: PrivyServerAdapterConfig;
  private apiUrl: string;

  constructor(config: PrivyServerAdapterConfig) {
    super();
    this.config = config;
    this.apiUrl = config.apiUrl || 'https://auth.privy.io';
  }

  async signL1Action(params: SignL1ActionParams): Promise<`0x${string}`> {
    const eip712Message = {
      domain: EIP712_DOMAINS.EXCHANGE,
      primaryType: 'Agent',
      types: {
        EIP712Domain: [
          { name: 'name', type: 'string' },
          { name: 'version', type: 'string' },
          { name: 'chainId', type: 'uint256' },
          { name: 'verifyingContract', type: 'address' },
        ],
        ...EIP712_TYPES.AGENT,
      },
      message: {
        source: params.isTestnet ? 'b' : 'a',
        connectionId: params.actionHash,
      },
    };

    debugLog('[Privy Server Adapter] Signing EIP-712 message via Privy API');
    debugLog('[Privy Server Adapter] User ID:', this.config.userId);
    debugLog('[Privy Server Adapter] Wallet address:', this.config.walletAddress);

    try {
      const signature = await this.callPrivySignTypedData(eip712Message);
      debugLog('[Privy Server Adapter] Received signature:', signature);
      return signature as `0x${string}`;
    } catch (error) {
      errorLog('[Privy Server Adapter] Failed to sign:', error);
      throw new Error(
        `Failed to sign with Privy delegated wallet: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  async isReady(): Promise<boolean> {
    return !!(
      this.config.userId &&
      this.config.walletAddress &&
      this.config.appId &&
      this.config.appSecret
    );
  }

  getWalletAddress(): string {
    return this.config.walletAddress;
  }

  /**
   * Call Privy's sign typed data API endpoint
   * Reference: https://docs.privy.io/guide/server/wallets/delegated-actions/sign
   */
  private async callPrivySignTypedData(
    typedData: Record<string, unknown>
  ): Promise<string> {
    const url = `${this.apiUrl}/api/v1/wallets/${this.config.walletAddress}/sign_typed_data`;

    const requestBody: SignTypedDataRequest = {
      chain_type: 'ethereum',
      typed_data: typedData,
      address: this.config.walletAddress,
    };

    // Privy uses HTTP Basic Auth with app_id:app_secret
    const authHeader = `Basic ${Buffer.from(
      `${this.config.appId}:${this.config.appSecret}`
    ).toString('base64')}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader,
        'privy-app-id': this.config.appId,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Privy API error (${response.status}): ${errorText}`
      );
    }

    const data = (await response.json()) as SignTypedDataResponse;
    return data.signature;
  }
}
